import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { getActiveMenuItemSelector, menusSlice } from '»components/Menus';
import { useOrdStatusLabel } from '»utils/customHooks';
import { TrlDateFormatted, TrlTimeFormatted } from '»utils/date';
import { getEndDecorator } from '»utils/endDecorator';
import { getDecimalDigits, TrlNumberFormatted } from '»utils/number';
import { getOperationLabel } from '»utils/trading/trading.utils';
import { TrlFieldDefaultValue } from '»utils/trlFieldDefaultValue';

import { ListItemTextStyled } from './TrlStatusText.styles';

export const TrlStatusText = ({
  value,
  type,
  field,
  priceRepresentation,
  currency,
  textTransform,
  color,
  onClick,
  roundPrice,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const activeMenuItem = useSelector(getActiveMenuItemSelector);

  const { getOrdStatusLabel } = useOrdStatusLabel();

  const openFilesPreview = ({ fileId }) => {
    const proofPage = {
      path: '',
      menuPath: '',
    };

    if (activeMenuItem === 'TRADING:PRIMARY') {
      proofPage.path = '/Trading/Primary/ProofPreview';
      proofPage.menuPath = 'TRADING:PRIMARY:PROOF';
    } else if (activeMenuItem === 'TRADING:BOLSA') {
      proofPage.path = '/Trading/StockExchange/ProofPreview';
      proofPage.menuPath = 'TRADING:BOLSA:PROOF';
    } else if (activeMenuItem === 'TRADING:BOLSA:DETAILS') {
      proofPage.path = '/Trading/StockExchange/Details/ProofPreview';
      proofPage.menuPath = 'TRADING:BOLSA:DETAILS:PROOF';
    } else if (activeMenuItem === 'TRADING:FUNDS') {
      proofPage.path = '/Trading/Funds/ProofPreview';
      proofPage.menuPath = 'TRADING:FUNDS:PROOF';
    } else if (activeMenuItem === 'TRADING:FUNDS:DETAILS') {
      proofPage.path = '/Trading/Funds/Details/ProofPreview';
      proofPage.menuPath = 'TRADING:FUNDS:DETAILS:PROOF';
    }

    navigate(proofPage.path, {
      state: {
        fileId,
        fileName: 'TRLT_UI-PROOF_PDF',
      },
    });

    dispatch(
      menusSlice.actions.setActiveMenuItem({
        activeMenuItem: proofPage.menuPath,
      }),
    );
  };

  switch (type?.toUpperCase()) {
    case 'DATE':
      return (
        <ListItemTextStyled
          primary={
            value ? <TrlDateFormatted date={value} /> : <TrlFieldDefaultValue />
          }
          textTransform={textTransform}
          color={color}
        />
      );
    case 'DATETIME':
      return (
        <ListItemTextStyled
          primary={
            value ? (
              <>
                <TrlDateFormatted date={value} />
                {' | '}
                <TrlTimeFormatted
                  value={value}
                  hour="numeric"
                  minute="numeric"
                  second="numeric"
                />
              </>
            ) : (
              <TrlFieldDefaultValue />
            )
          }
          textTransform={textTransform}
          color={color}
        />
      );
    case 'OPERATION': {
      const operationLabel = getOperationLabel(value);

      return (
        <ListItemTextStyled
          primary={
            operationLabel ? (
              <FormattedMessage id={operationLabel} />
            ) : (
              <TrlFieldDefaultValue />
            )
          }
          textTransform={textTransform}
          color={color}
        />
      );
    }
    case 'ORDERTYPE': {
      let label = '';

      if (value === '1' || value === '2') {
        label = 'TRLT_UI-NORMAL';
      } else {
        label = 'TRLT_UI-STOP';
      }

      return (
        <ListItemTextStyled
          primary={
            <FormattedMessage
              id={label}
              textTransform={textTransform}
              color={color}
            />
          }
        />
      );
    }
    case 'NUMERIC': {
      if (field === 'price' && (!value || value === 0))
        return (
          <ListItemTextStyled
            primary={<FormattedMessage id="TRLT_UI-MARKET" />}
          />
        );

      const endDecorator = priceRepresentation
        ? getEndDecorator(priceRepresentation, field, currency)
        : ` ${currency}`;

      return (
        <ListItemTextStyled
          primary={
            <TrlNumberFormatted
              value={value}
              decimalDigits={getDecimalDigits(roundPrice)}
              fontFamily="Roboto"
              endDecorator={endDecorator}
            />
          }
          textTransform={textTransform}
          color={color}
        />
      );
    }
    case 'OPENFILESPREVIEW':
      return (
        <ListItemTextStyled
          primary={
            value ? (
              <FormattedMessage id="TRLT_UI-OPEN" />
            ) : (
              <TrlFieldDefaultValue />
            )
          }
          underline={!!value}
          onClick={() => {
            if (value)
              openFilesPreview({
                fileId: value,
              });
          }}
          textTransform={textTransform}
          color={color}
          cursor="pointer"
        />
      );
    case 'OPENFILESMODAL':
      return (
        <ListItemTextStyled
          primary={<FormattedMessage id="TRLT_UI-OPEN" />}
          underline
          onClick={onClick}
          textTransform={textTransform}
          color={color}
          cursor="pointer"
        />
      );
    case 'STATUS':
      return (
        <ListItemTextStyled
          primary={getOrdStatusLabel(value, 'label')}
          textTransform={textTransform}
          color={color}
        />
      );
    default:
      return (
        <ListItemTextStyled
          primary={value || <TrlFieldDefaultValue />}
          textTransform={textTransform}
          color={color}
        />
      );
  }
};

TrlStatusText.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string,
  field: PropTypes.string,
  priceRepresentation: PropTypes.string,
  currency: PropTypes.string,
  textTransform: PropTypes.string,
  color: PropTypes.string,
  onClick: PropTypes.func,
  roundPrice: PropTypes.number,
};
